import { useEffect, useMemo, useState } from 'react';
import {
  Badge, Button, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import DateRangePresets from './DateRangePresets';
import EmptyState from './EmptyState';
import ErrorRetry from './ErrorRetry';
import PrintButton from './PrintButton';
import PrintHeader from './PrintHeader';
import TableSkeleton from './TableSkeleton';
import { fetchAccountStatement } from '../api/statement';
import { localYMD } from '../lib/format';
import { t } from '../lib/i18n';
import type { AccountStatement as Stmt } from '../api/types';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });
const fmtPct = new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function dateNDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localYMD(d);
}

/**
 * v2.43 — renders one account's Estado de cuenta (header, period summary,
 * aging buckets, transaction ledger) for a given store+account. Controlled by
 * sid+anum; owns its own date window + fetch so it drops into both the
 * dedicated /estado route and the Customer-page tab unchanged.
 */
export default function AccountStatement({ sid, anum }: { sid: number; anum: number }) {
  // Default wide (5y) so the statement shows the full open balance + aging —
  // accounts can carry overdue items older than a year, and a short default
  // would hide exactly the aged debt accounting cares about. Narrow with the
  // date picker for a specific period.
  const [from, setFrom] = useState(dateNDaysAgo(1825));
  const [to, setTo] = useState(dateNDaysAgo(0));
  const [data, setData] = useState<Stmt | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () => {
    setErr(null);
    setData(null);
    fetchAccountStatement({ sid, anum, from, to })
      .then(setData)
      .catch((e: Error) => setErr(e.message ?? String(e)));
  };
  useEffect(() => { load(); }, [sid, anum, from, to]);

  // Overdue = everything past "corriente" and not future (buckets 31-60..90+).
  const overdue = useMemo(() => {
    if (!data) return 0;
    return data.aging
      .filter((b) => b.title !== 'Corriente' && b.title !== 'Futuros')
      .reduce((s, b) => s + b.amount_mxn, 0);
  }, [data]);

  function downloadCsv() {
    if (!data) return;
    const esc = (v: string | number) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows: string[] = [];
    rows.push(['Tienda', 'Fecha', 'Vence', 'Tipo', 'Factura', 'OC', 'Descripcion', 'Importe', 'Saldo', 'Balance'].join(','));
    for (const l of data.lines) {
      rows.push([l.sid, l.tdate, l.ddate ?? '', l.type_label, l.inum, l.po, l.tdesc, l.oamt_mxn, l.due_mxn, l.balance_mxn].map(esc).join(','));
    }
    const blob = new Blob(['﻿' + rows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `estado_cuenta_${data.header.sid}_${data.header.anum}_${data.header.run_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Build a sensible subtitle for the print header: account name + run date.
  const printSubtitle = data
    ? `${data.header.name} · ${t.statement.asOf} ${data.header.run_date}`
    : undefined;

  return (
    <div className="space-y-4">
      {/* Toolbar — no-print: date range + CSV + print button */}
      <Card className="flex flex-col md:flex-row md:items-center gap-3 no-print">
        <DateRangePresets from={from} to={to} onChange={({ from: f, to: t2 }) => { setFrom(f); setTo(t2); }} />
        <div className="flex gap-2 md:ml-auto">
          <Button size="xs" variant="secondary" disabled={!data || data.lines.length === 0} onClick={downloadCsv}>
            {t.statement.downloadCsv}
          </Button>
          <PrintButton
            documentTitle={data ? `Estado de cuenta - ${data.header.anum}` : 'Estado de cuenta'}
          />
        </div>
      </Card>

      {err && <ErrorRetry message={err} onRetry={load} />}
      {!err && data === null && <TableSkeleton rows={6} cols={7} />}

      {!err && data && (
        // print-region: wraps account header + summary + aging + ledger.
        // The toolbar above is outside this div (no-print) so only the
        // document body appears in the PDF output.
        <div className="print-region space-y-4">
          <PrintHeader
            title={t.statement.title}
            subtitle={printSubtitle}
          />

          {/* Account + store header */}
          <Card>
            <div className="flex flex-col md:flex-row md:justify-between gap-2">
              <div>
                <Title className="text-base">{data.header.name}</Title>
                <Text className="text-xs text-tremor-content">#{data.header.anum} · {data.header.address || '—'}</Text>
              </div>
              <div className="md:text-right">
                <Text className="text-xs text-tremor-content">{data.header.store_name} · T{data.header.sid}</Text>
                <Text className="text-xs text-tremor-content">
                  {t.statement.asOf} {data.header.run_date}
                  {data.header.finance_rate > 0 && <> · {t.statement.financeRate}: {fmtPct.format(data.header.finance_rate)}%</>}
                </Text>
              </div>
            </div>
          </Card>

          {/* Period summary */}
          <Card className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <Kpi label={t.statement.summary.prev} value={fmtMxn.format(data.summary.prev_balance_mxn)} />
            <Kpi label={t.statement.summary.charges} value={fmtMxn.format(data.summary.charges_mxn)} />
            <Kpi label={t.statement.summary.credits} value={fmtMxn.format(data.summary.credits_mxn)} />
            <Kpi label={t.statement.summary.payments} value={fmtMxn.format(data.summary.payments_mxn)} />
            <Kpi label={t.statement.summary.other} value={fmtMxn.format(data.summary.other_mxn)} />
            <Kpi label={t.statement.summary.total} value={fmtMxn.format(data.total_mxn)} strong />
          </Card>

          {/* Aging buckets */}
          <Card>
            <div className="flex items-baseline justify-between">
              <Title className="text-base">{t.statement.agingTitle}</Title>
              {overdue > 0 && (
                <Text className="text-sm text-red-600 dark:text-red-400 font-semibold">
                  {t.statement.overdue}: {fmtMxn.format(overdue)}
                </Text>
              )}
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-3">
              {data.aging.map((b) => (
                <div key={b.title}>
                  <div className="text-tremor-content text-xs">{b.title}</div>
                  <div className={'tabular-nums font-medium ' +
                    (b.amount_mxn !== 0 && b.title !== 'Corriente' && b.title !== 'Futuros'
                      ? 'text-amber-700 dark:text-amber-400' : 'text-tremor-content-strong')}>
                    {fmtMxn.format(b.amount_mxn)}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Ledger */}
          {data.lines.length === 0 ? (
            <EmptyState message={t.statement.empty} />
          ) : (
            <Card className="p-0 overflow-hidden">
              <div className="px-4 pt-3"><Title className="text-base">{t.statement.ledgerTitle}</Title></div>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{t.statement.cols.date}</TableHeaderCell>
                    <TableHeaderCell>{t.statement.cols.due}</TableHeaderCell>
                    <TableHeaderCell>{t.statement.cols.type}</TableHeaderCell>
                    <TableHeaderCell>{t.statement.cols.inum}</TableHeaderCell>
                    <TableHeaderCell>{t.statement.cols.po}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.statement.cols.amount}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.statement.cols.balance}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.statement.cols.running}</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.lines.map((l, i) => (
                    <TableRow key={`${l.sid}-${l.inum}-${l.tdate}-${i}`}>
                      <TableCell>{l.tdate}</TableCell>
                      <TableCell>{l.ddate ?? '—'}</TableCell>
                      <TableCell><Badge color={l.due_mxn < 0 ? 'emerald' : 'gray'}>{l.type_label}</Badge></TableCell>
                      <TableCell>{l.inum || '—'}</TableCell>
                      <TableCell>{l.po || '—'}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtMxn.format(l.oamt_mxn)}</TableCell>
                      <TableCell className={'text-right tabular-nums ' + (l.due_mxn < 0 ? 'text-emerald-700 dark:text-emerald-400' : '')}>
                        {fmtMxn.format(l.due_mxn)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{fmtMxn.format(l.balance_mxn)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <div className="text-tremor-content text-xs">{label}</div>
      <div className={'tabular-nums ' + (strong ? 'text-tremor-content-strong text-lg font-semibold' : 'text-tremor-content-strong font-medium')}>
        {value}
      </div>
    </div>
  );
}
